/**
 * Tab Fases & Entregáveis — tabela com actions inline + expansão
 * mostrando descrição detalhada + lançamentos recentes.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { fasesAPI, projetosAPI } from '../../../services/inovacaoAPI';
import { authAPI } from '../../../services/api';
import { ChevronDown, ChevronRight, Play, CheckCircle, AlertTriangle, Ban, Plus, PlusCircle, Pencil, Trash2 } from 'lucide-react';
import ComentariosFase from './ComentariosFase';
import TimelineEventos from './TimelineEventos';
import TarefasFase from './TarefasFase';
import ModalCampos from '../ModalCampos';
import { mensagemErro, rotuloEstadoFase } from '../labels';

const MODAL_DEF = {
  horas: {
    titulo: 'Lançar horas', confirmLabel: 'Lançar', confirmCor: 'var(--brand, #20a490)',
    campos: [
      { nome: 'horas', label: 'Horas', tipo: 'number', required: true, step: '0.5', min: '0' },
      { nome: 'descricao', label: 'Descrição (opcional)', tipo: 'textarea', placeholder: 'O que foi feito' },
    ],
  },
  risco: {
    titulo: 'Marcar fase em risco', confirmLabel: 'Marcar risco', confirmCor: '#e67e22',
    campos: [{ nome: 'motivo', label: 'Motivo do risco', tipo: 'textarea', required: true }],
  },
  bloqueio: {
    titulo: 'Bloquear fase', confirmLabel: 'Bloquear', confirmCor: '#e74c3c',
    campos: [{ nome: 'motivo', label: 'Motivo do bloqueio', tipo: 'textarea', required: true }],
  },
  editar_fase: {
    titulo: 'Editar fase', confirmLabel: 'Salvar', confirmCor: 'var(--brand, #20a490)',
    campos: [
      { nome: 'nome', label: 'Nome da fase', tipo: 'text', required: true },
      { nome: 'horas_estimadas', label: 'Horas estimadas', tipo: 'number', step: '0.5', min: '0' },
      { nome: 'data_inicio_planejada', label: 'Início (previsto)', tipo: 'date' },
      { nome: 'data_fim_planejada', label: 'Fim (previsto)', tipo: 'date' },
      { nome: 'descricao', label: 'Descrição', tipo: 'textarea' },
    ],
  },
  excluir_fase: {
    titulo: 'Excluir fase', confirmLabel: 'Excluir', confirmCor: '#e74c3c', campos: [],
  },
};

const COR = {
  planejada: '#95a5a6', em_execucao: '#3498db',
  concluida: '#27ae60', em_risco: '#e67e22', bloqueada: '#e74c3c',
};

export default function Fases({ projeto, fases, recarregar }) {
  const [expandida, setExpandida] = useState(null);
  const [lancamentos, setLancamentos] = useState({});
  const [mostrarForm, setMostrarForm] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(null); // { tipo: 'horas'|'risco'|'bloqueio', fase }
  const [form, setForm] = useState({
    nome: '', categoria: '', horas_estimadas: '',
    responsavel_id: '', descricao: '',
  });

  useEffect(() => {
    authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {});
  }, []);

  const podeAdicionar = !['concluido', 'cancelado'].includes(projeto.estado);
  const serEscopoAdicional = projeto.estado !== 'planejado' && podeAdicionar;

  async function criarFase(e) {
    e.preventDefault();
    if (!form.nome || !form.horas_estimadas) {
      toast.error('Nome e horas são obrigatórios'); return;
    }
    const ordemMax = Math.max(0, ...fases.filter((f) => !f.eh_buffer).map((f) => f.ordem));
    try {
      await projetosAPI.adicionarFase(projeto.id, {
        nome: form.nome,
        categoria: form.categoria || null,
        descricao: form.descricao || null,
        horas_estimadas: form.horas_estimadas,
        responsavel_id: form.responsavel_id ? parseInt(form.responsavel_id) : null,
        ordem: ordemMax + 1,
        eh_buffer: false,
      });
      toast.success(serEscopoAdicional ? 'Fase adicional criada' : 'Fase criada');
      setForm({ nome: '', categoria: '', horas_estimadas: '', responsavel_id: '', descricao: '' });
      setMostrarForm(false);
      await recarregar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao criar fase'));
    }
  }

  async function abrir(faseId) {
    if (expandida === faseId) { setExpandida(null); return; }
    setExpandida(faseId);
    if (!lancamentos[faseId]) {
      try {
        const l = await fasesAPI.listarLancamentos(faseId);
        setLancamentos((x) => ({ ...x, [faseId]: l }));
      } catch { /* noop */ }
    }
  }

  async function acao(fn, msg) {
    try { await fn(); toast.success(msg); await recarregar(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || 'Erro'); }
  }

  // Atribui/troca o responsável de uma fase existente (alimenta Gantt + Carga de Pessoas)
  async function atribuirResponsavel(fase, valor) {
    try {
      await fasesAPI.atualizar(fase.id, { responsavel_id: valor ? parseInt(valor) : null });
      toast.success('Responsável atualizado');
      await recarregar();
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao atribuir responsável'));
    }
  }

  // Confirmação do modal (substitui os prompt() de lançar horas / risco / bloqueio)
  async function confirmarModal(vals) {
    if (!modal) return;
    const { tipo, fase } = modal;
    try {
      if (tipo === 'horas') {
        await fasesAPI.criarLancamento(fase.id, {
          data: new Date().toISOString().slice(0, 10),
          horas: vals.horas, descricao: vals.descricao || '',
        });
        toast.success('Lançamento registrado');
        setLancamentos((x) => { const copy = { ...x }; delete copy[fase.id]; return copy; });
      } else if (tipo === 'risco') {
        await fasesAPI.marcarRisco(fase.id, vals.motivo);
        toast.success('Fase marcada em risco');
      } else if (tipo === 'bloqueio') {
        await fasesAPI.bloquear(fase.id, vals.motivo);
        toast.success('Fase bloqueada');
      } else if (tipo === 'editar_fase') {
        const patch = { nome: vals.nome };
        if (vals.horas_estimadas !== undefined && vals.horas_estimadas !== '') {
          patch.horas_estimadas = parseFloat(vals.horas_estimadas);
        }
        patch.data_inicio_planejada = vals.data_inicio_planejada || null;
        patch.data_fim_planejada = vals.data_fim_planejada || null;
        patch.descricao = vals.descricao ?? null;
        await fasesAPI.atualizar(fase.id, patch);
        toast.success('Fase atualizada');
      } else if (tipo === 'excluir_fase') {
        await fasesAPI.deletar(fase.id);
        toast.success('Fase removida');
      }
      setModal(null);
      await recarregar();
      if (tipo === 'horas' && expandida === fase.id) abrir(fase.id); // refaz o load
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro'));
    }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #ecf0f1',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>
          {fases.length} fase(s) · {fases.filter((f) => f.estado === 'concluida').length} concluída(s)
        </h3>
        {podeAdicionar && (
          <button onClick={() => setMostrarForm(!mostrarForm)}
                  style={{
                    background: serEscopoAdicional ? '#ff7a00' : 'var(--brand, #20a490)',
                    color: serEscopoAdicional ? '#fff' : 'var(--brand-ink, #fff)', padding: '8px 14px', border: 'none',
                    borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
            <PlusCircle size={14} />
            {serEscopoAdicional ? 'Adicionar escopo (desvio)' : 'Adicionar fase'}
          </button>
        )}
      </div>

      {mostrarForm && (
        <form onSubmit={criarFase} style={{
          padding: '16px 20px', background: serEscopoAdicional ? '#fff4e6' : '#f8f9fa',
          borderBottom: '1px solid #ecf0f1',
          borderLeft: serEscopoAdicional ? '4px solid #ff7a00' : 'none',
        }}>
          {serEscopoAdicional && (
            <div style={{ color: '#c0392b', fontSize: '0.82rem', marginBottom: 8 }}>
              ⚠️ Projeto já iniciou. Esta fase será marcada como <b>escopo adicional</b> (aparece em laranja no Gantt e na retrospectiva).
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: 8, marginBottom: 8 }}>
            <input required placeholder="Nome da fase *" value={form.nome}
                   onChange={(e) => setForm({ ...form, nome: e.target.value })}
                   style={inputStyle} />
            <input placeholder="Categoria" value={form.categoria}
                   onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                   style={inputStyle} />
            <input required type="number" step="0.5" min="0.5" placeholder="Horas *" value={form.horas_estimadas}
                   onChange={(e) => setForm({ ...form, horas_estimadas: e.target.value })}
                   style={inputStyle} />
            <select value={form.responsavel_id}
                    onChange={(e) => setForm({ ...form, responsavel_id: e.target.value })}
                    style={inputStyle}>
              <option value="">Sem responsável</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
            </select>
          </div>
          <textarea placeholder="Descrição (opcional)" value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    style={{ ...inputStyle, width: '100%', minHeight: 50, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit"
                    style={{
                      background: serEscopoAdicional ? '#ff7a00' : 'var(--brand, #20a490)',
                      color: serEscopoAdicional ? '#fff' : 'var(--brand-ink, #fff)', padding: '8px 18px', border: 'none',
                      borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem',
                    }}>
              Criar fase
            </button>
            <button type="button" onClick={() => setMostrarForm(false)}
                    style={{
                      background: '#ecf0f1', color: '#555', padding: '8px 18px',
                      border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem',
                    }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: '#f8f9fa' }}>
          <tr>
            <th style={thStyle}></th>
            <th style={thStyle}>#</th>
            <th style={thStyle}>Nome</th>
            <th style={thStyle}>Categoria</th>
            <th style={thStyle}>Horas</th>
            <th style={thStyle}>Responsável</th>
            <th style={thStyle}>Datas previstas</th>
            <th style={thStyle}>Estado</th>
            <th style={thStyle}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {fases.map((f) => {
            const aberta = expandida === f.id;
            const temTar = f.tem_tarefas;
            const horasEst = parseFloat(f.horas_estimadas || 0);
            // "Consumido" da fase vem dos lançamentos (não apontamos horas por tarefa).
            const horasReal = parseFloat(f.horas_consumidas || 0);
            const pctHoras = horasEst > 0 ? Math.round((horasReal / horasEst) * 100) : 0;
            return (
              <React.Fragment key={f.id}>
                <tr style={{
                  borderTop: '1px solid #ecf0f1',
                  background: aberta ? '#f8f9fa' : (f.eh_buffer ? '#fffaf0' : '#fff'),
                }}>
                  <td style={{ ...tdStyle, width: 28, cursor: 'pointer' }}
                      onClick={() => abrir(f.id)}>
                    {aberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </td>
                  <td style={tdStyle}>{f.ordem}</td>
                  <td style={tdStyle}>
                    {f.eh_buffer && (
                      <span style={{ background: '#f39c12', color: '#fff', padding: '1px 6px',
                                     borderRadius: 4, fontSize: '0.68rem', marginRight: 6 }}>BUFFER</span>
                    )}
                    {f.eh_escopo_adicional && (
                      <span
                        title={f.escopo_adicionado_em
                          ? `Escopo adicional, inserido em ${new Date(f.escopo_adicionado_em).toLocaleDateString('pt-BR')}`
                          : 'Escopo adicional'}
                        style={{ background: '#ff7a00', color: '#fff', padding: '1px 6px',
                                 borderRadius: 4, fontSize: '0.68rem', marginRight: 6 }}>
                        + ESCOPO
                      </span>
                    )}
                    <span style={{ fontWeight: 500 }}>{f.nome}</span>
                  </td>
                  <td style={tdStyle}>
                    {f.categoria && (
                      <span style={{ background: '#ecf0f1', padding: '2px 8px', borderRadius: 4,
                                     fontSize: '0.75rem', color: '#555' }}>{f.categoria}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: '0.85rem' }}>{horasReal}h / {horasEst}h</div>
                    {horasEst > 0 && (
                      <div style={{ height: 4, background: '#ecf0f1', borderRadius: 2, marginTop: 2, width: 80 }}>
                        <div style={{
                          height: '100%', width: `${Math.min(100, pctHoras)}%`,
                          background: pctHoras > 100 ? '#e74c3c' : 'var(--brand, #20a490)',
                        }} />
                      </div>
                    )}
                    {temTar && (
                      <div style={{ fontSize: '0.7rem', color: '#95a5a6', marginTop: 2 }}
                           title="Horas e progresso somados das tarefas">
                        {f.tarefas_feitas}/{f.tarefas_total} tarefas · das tarefas
                      </div>
                    )}
                    {f.horas_baseline != null && (() => {
                      const base = parseFloat(f.horas_baseline);
                      const delta = horasEst - base;
                      return (
                        <div style={{ fontSize: '0.7rem', marginTop: 2,
                                      color: delta > 0 ? '#e67e22' : '#95a5a6' }}
                             title="Comparado ao plano aprovado (baseline)">
                          plano {base}h{delta !== 0 ? ` · ${delta > 0 ? '+' : ''}${delta.toFixed(1)}h desvio` : ' · no plano'}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.85rem' }}>
                    {temTar ? (
                      f.responsaveis?.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }} title="Responsáveis derivados das tarefas">
                          {f.responsaveis.map((u) => (
                            <span key={u.id} style={{ background: '#eef6fc', color: '#2980b9', borderRadius: 10,
                                                      padding: '2px 8px', fontSize: '0.74rem' }}>{primeiroNome(u.nome)}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#bdc3c7', fontSize: '0.8rem' }}>defina nas tarefas</span>
                      )
                    ) : (
                      <select
                        value={f.responsavel?.id || ''}
                        onChange={(e) => atribuirResponsavel(f, e.target.value)}
                        title="Atribuir/trocar responsável"
                        style={{
                          padding: '4px 6px', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'inherit',
                          maxWidth: 160, cursor: 'pointer',
                          border: f.responsavel ? '1px solid #ddd' : '1px solid #f0c089',
                          color: f.responsavel ? '#2c3e50' : '#b9772a',
                          background: f.responsavel ? '#fff' : '#fff8ef',
                        }}
                      >
                        <option value="">Sem responsável</option>
                        {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#7f8c8d' }}>
                    {f.data_inicio_planejada} → {f.data_fim_planejada}
                    {temTar && <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>das tarefas</div>}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      background: rotuloEstadoFase(f.estado).cor, color: '#fff',
                      padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem',
                    }}>{rotuloEstadoFase(f.estado).label}</span>
                  </td>
                  <td style={tdStyle}>
                    <IconBtn cor="#7f8c8d" icon={<Pencil size={12} />} label="editar"
                             onClick={() => setModal({ tipo: 'editar_fase', fase: f })} />
                    {f.estado === 'planejada' && (
                      <>
                        <IconBtn cor="#3498db" icon={<Play size={12} />} label="iniciar"
                                 onClick={() => acao(() => fasesAPI.iniciar(f.id), 'Iniciada')} />
                        <IconBtn cor="#e74c3c" icon={<Trash2 size={12} />} label="excluir"
                                 onClick={() => setModal({ tipo: 'excluir_fase', fase: f })} />
                      </>
                    )}
                    {['em_execucao', 'em_risco'].includes(f.estado) && (
                      <>
                        <IconBtn cor="var(--brand, #20a490)" icon={<Plus size={12} />} label="lançar h"
                                 onClick={() => setModal({ tipo: 'horas', fase: f })} />
                        <IconBtn cor="#27ae60" icon={<CheckCircle size={12} />} label="concluir"
                                 onClick={() => acao(() => fasesAPI.concluir(f.id), 'Concluída')} />
                      </>
                    )}
                    {f.estado === 'em_execucao' && (
                      <IconBtn cor="#e67e22" icon={<AlertTriangle size={12} />} label="risco"
                               onClick={() => setModal({ tipo: 'risco', fase: f })} />
                    )}
                    {['em_execucao', 'em_risco'].includes(f.estado) && (
                      <IconBtn cor="#e74c3c" icon={<Ban size={12} />} label="bloq."
                               onClick={() => setModal({ tipo: 'bloqueio', fase: f })} />
                    )}
                    {f.estado === 'bloqueada' && (
                      <IconBtn cor="#3498db" icon={<Play size={12} />} label="desbloq."
                               onClick={() => acao(() => fasesAPI.desbloquear(f.id), 'Desbloqueada')} />
                    )}
                  </td>
                </tr>
                {aberta && (
                  <tr>
                    <td colSpan={9} style={{ padding: 20, background: '#f8f9fa', borderTop: '1px solid #ecf0f1' }}>
                      <ExpansaoFase fase={f} lancamentos={lancamentos[f.id] || []} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {modal && (
        <ModalCampos
          titulo={MODAL_DEF[modal.tipo].titulo}
          descricao={modal.tipo === 'excluir_fase'
            ? `A fase "${modal.fase?.nome}" será removida. Esta ação não tem volta.`
            : (modal.tipo === 'editar_fase' && modal.fase?.tem_tarefas
                ? `${modal.fase?.nome}: horas e datas são somadas das tarefas (não editáveis aqui).`
                : modal.fase?.nome)}
          campos={
            modal.tipo === 'editar_fase' && modal.fase?.tem_tarefas
              ? MODAL_DEF.editar_fase.campos.filter(
                  (c) => !['horas_estimadas', 'data_inicio_planejada', 'data_fim_planejada'].includes(c.nome))
              : MODAL_DEF[modal.tipo].campos
          }
          valoresIniciais={modal.tipo === 'editar_fase' ? {
            nome: modal.fase.nome || '',
            horas_estimadas: modal.fase.horas_estimadas || '',
            data_inicio_planejada: modal.fase.data_inicio_planejada || '',
            data_fim_planejada: modal.fase.data_fim_planejada || '',
            descricao: modal.fase.descricao || '',
          } : undefined}
          confirmLabel={MODAL_DEF[modal.tipo].confirmLabel}
          confirmCor={MODAL_DEF[modal.tipo].confirmCor}
          onConfirm={confirmarModal}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}

function ExpansaoFase({ fase, lancamentos }) {
  const temDescricao = fase.descricao || fase.por_que_importa || fase.resultado_esperado;
  return (
    <div>
      {temDescricao && (
        <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #ecf0f1' }}>
          {fase.descricao && (<>
            <h4 style={h4Style}>O que será feito</h4>
            <p style={pStyle}>{fase.descricao}</p>
          </>)}
          {fase.por_que_importa && (<>
            <h4 style={h4Style}>Por que importa</h4>
            <p style={pStyle}>{fase.por_que_importa}</p>
          </>)}
          {fase.resultado_esperado && (<>
            <h4 style={h4Style}>Resultado esperado</h4>
            <p style={pStyle}>{fase.resultado_esperado}</p>
          </>)}
        </div>
      )}
      <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #ecf0f1' }}>
        <TarefasFase faseId={fase.id} projetoId={fase.projeto_id} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.2fr', gap: 24 }}>
        <div>
          <h4 style={h4Style}>Lançamentos ({lancamentos.length})</h4>
          {lancamentos.length === 0 ? (
            <p style={{ color: '#bdc3c7', fontSize: '0.85rem' }}>Nenhum lançamento.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem' }}>
              {lancamentos.map((l) => (
                <li key={l.id} style={{ padding: '6px 0', borderBottom: '1px solid #ecf0f1' }}>
                  <div><b>{l.horas}h</b> · {l.data} · <span style={{ color: '#7f8c8d' }}>{l.usuario?.nome}</span></div>
                  {l.descricao && <div style={{ color: '#555', fontSize: '0.8rem' }}>{l.descricao}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <ComentariosFase faseId={fase.id} />
        </div>
        <div>
          <TimelineEventos tipo="fase" id={fase.id} />
        </div>
      </div>
    </div>
  );
}

function IconBtn({ cor, icon, label, onClick }) {
  return (
    <button onClick={onClick}
            style={{
              padding: '4px 8px', background: cor, color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer',
              fontSize: '0.72rem', marginRight: 4, display: 'inline-flex',
              alignItems: 'center', gap: 3, marginBottom: 2,
            }}>
      {icon} {label}
    </button>
  );
}

function primeiroNome(nome) {
  return String(nome || '').split(' ')[0];
}

const thStyle = { textAlign: 'left', padding: '12px 10px', fontSize: '0.78rem',
                  color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 };
const tdStyle = { padding: '12px 10px', verticalAlign: 'middle' };
const inputStyle = { padding: 8, borderRadius: 4, border: '1px solid #ddd',
                     fontSize: '0.88rem', fontFamily: 'inherit' };
const h4Style = { margin: '0 0 6px', fontSize: '0.85rem', color: '#2c3e50',
                  textTransform: 'uppercase', letterSpacing: 0.5 };
const pStyle = { margin: '0 0 16px', color: '#555', fontSize: '0.88rem', lineHeight: 1.5 };
